import { AssessmentEditState } from './useAssessmentState';

interface UseContentManagementProps {
  state: AssessmentEditState;
  setConfirmingVersionChange: (confirming: boolean) => void;
  saveSettings: () => Promise<boolean>;
  saveContent: () => Promise<boolean>;
}

export function useContentManagement({ 
  state, 
  setConfirmingVersionChange, 
  saveSettings, 
  saveContent 
}: UseContentManagementProps) {
  
  // Check if content is being changed
  const isContentChanged = (): boolean => {
    if (!state.assessment) return false;
    
    // Simple check for now - if categories are different
    const originalJSON = JSON.stringify(state.assessment.categories);
    const currentJSON = JSON.stringify(state.categories);
    
    return originalJSON !== currentJSON;
  };

  // Main save handler that determines what type of save to perform
  const handleSubmit = async (): Promise<boolean> => {
    // If assessment is in draft mode, we can save everything without versioning
    if (!state.assessment || state.assessment.status === 'draft') {
      const settingsSaved = await saveSettings();
      const contentSaved = await saveContent();
      return settingsSaved && contentSaved;
    }
    
    // For published assessments, check if content changes need versioning
    if (isContentChanged()) {
      // Show version confirmation dialog before saving
      setConfirmingVersionChange(true);
      return false; // Don't proceed with save until confirmed
    }
    
    // If no content changes, just save settings
    return await saveSettings();
  };

  // Cancel version change confirmation - keep local changes intact
  const cancelContentChanges = () => {
    setConfirmingVersionChange(false);
    // DO NOT reset categories - keep the user's local changes
    // The user should be able to continue editing after canceling
  };

  // Removed aggressive auto-save - no longer automatically triggers version dialogs
  const autoSaveContent = async () => {
    // This function is now a no-op to prevent aggressive auto-saving
    // Users will only see version dialogs when they explicitly save
  };

  return {
    isContentChanged,
    handleSubmit,
    cancelContentChanges,
    autoSaveContent,
  };
}
